import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BannerCarousel from "../components/BannerCarousel";
import CategoryGrid from "../components/CategoryGrid";
import AdCard from "../components/AdCard";
import TopSellersCarousel from "../components/TopSellersCarousel";
import SEOHead from "../components/SEOHead";
import { WebsiteSchema, OrganizationSchema, HowToSchema, FAQSchema, LocalBusinessSchema, SiteNavigationSchema } from "../components/SchemaMarkup";
import AEOContent, { HOMEPAGE_AEO_QUESTIONS } from "../components/AEOContent";
import { useLanguage } from "../i18n/LanguageContext";
import { Button } from "../components/ui/button";
import { ArrowRight, TrendingUp, Clock, Sparkles, Search, ShieldCheck, Users, Zap, MapPin, Gavel, Flame, Activity } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HomePage() {
  const { t } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [promotedAds, setPromotedAds] = useState([]);
  const [recentAds, setRecentAds] = useState([]);
  const [banners, setBanners] = useState([]);
  const [stats, setStats] = useState({ active_ads: 0, total_users: 0, live_auctions: 0, new_this_week: 0, verified_users: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesRes, promotedRes, recentRes, bannersRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/categories`),
        axios.get(`${API_URL}/api/ads/promoted?limit=8`),
        axios.get(`${API_URL}/api/ads?sort=newest&limit=12`),
        axios.get(`${API_URL}/api/banners?position=homepage`),
        axios.get(`${API_URL}/api/public/stats`).catch(() => ({ data: null }))
      ]);
      
      // Hide adult category (escorts) from PUBLIC homepage + Popular Categories grid.
      // Rationale: protects Google indexing (adult content sandboxed),
      // keeps compliance with Stripe / Viva Wallet ToS, and prevents brand dilution
      // for advertisers. The category page /category/escorts remains accessible directly.
      const publicCategories = (categoriesRes.data || []).filter(
        (c) => c.id !== "escorts"
      );
      setCategories(publicCategories);
      // Also exclude escort ads from the public featured lists on the homepage.
      const filterAdult = (ads) => (ads || []).filter(
        (a) => a && a.category_id !== "escorts"
      );
      setPromotedAds(filterAdult(promotedRes.data));
      setRecentAds(filterAdult(recentRes.data.ads || []));
      setBanners(bannersRes.data);
      if (statsRes.data) setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const homeFAQs = [
    { question: "Cum postez un anunt gratuit pe X67?", answer: "Creeaza un cont gratuit, alege categoria potrivita, adauga detaliile anuntului si publica. Este simplu si rapid!" },
    { question: "Este gratuit sa postezi anunturi pe X67 Digital?", answer: "Da, postarea de anunturi pe X67 Digital Media este complet gratuita. Poti posta nelimitat in orice categorie." },
    { question: "Ce categorii de anunturi sunt disponibile?", answer: "Avem categorii pentru Auto, Imobiliare, Electronice, Locuri de munca, Moda, Servicii, Animale si multe altele." },
    { question: "Cum contactez un vanzator?", answer: "Poti contacta vanzatorul direct prin sistemul de mesagerie al platformei sau prin numarul de telefon afisat in anunt." },
    { question: "Anunturile sunt verificate?", answer: "Da, toate anunturile trec printr-un proces de verificare automata si manuala pentru a asigura calitatea si siguranta utilizatorilor." }
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="home-page">
      <SEOHead 
        title="X67 Digital, Anunțuri Gratuite în România | Auto, Imobiliare, Electronice"
        description="X67 Digital - marketplace românesc gratuit cu licitații live, escrow integrat, AI vision și verificare KYC. Mașini second hand, apartamente, case, electronice, joburi în 368 de localități. Fără comision."
        keywords="anunturi gratuite, anunturi romania, masini second hand, auto sh, auto romania, apartamente de vanzare, case de vanzare, imobiliare romania, electronice ieftine, telefoane, laptopuri, locuri de munca, joburi romania, servicii, olx alternativa, publi24 alternativa, licitatii online, escrow romania"
        url="https://x67digital.com"
      />
      <WebsiteSchema />
      <OrganizationSchema />
      <LocalBusinessSchema city="România" />
      <SiteNavigationSchema />
      <HowToSchema />
      <FAQSchema faqs={homeFAQs} />
      <Header />

      <main>
        {/* Premium Hero Section */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 -z-10">
            <div className="absolute -top-40 -left-32 w-[500px] h-[500px] rounded-full bg-blue-500/20 blur-[120px]" />
            <div className="absolute top-20 -right-32 w-[500px] h-[500px] rounded-full bg-fuchsia-500/15 blur-[140px]" />
            <div className="absolute top-60 left-1/3 w-[400px] h-[400px] rounded-full bg-purple-500/10 blur-[100px]" />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 lg:pt-20 pb-8 sm:pb-12">
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs font-semibold mb-5" data-testid="hero-pill">
                <Sparkles className="w-3 h-3" />
                100% gratuit · zero comision la vânzare · plata sigură via Escrow
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.05] mb-4 sm:mb-5">
                Vinde și cumpără în <br />
                <span className="bg-gradient-to-r from-blue-400 via-fuchsia-400 to-purple-400 bg-clip-text text-transparent">
                  toată România
                </span>
              </h1>
              <p className="text-base sm:text-lg text-slate-300/90 leading-relaxed max-w-2xl mx-auto mb-7">
                Marketplace românesc cu licitații live, escrow integrat, AI care îți generează anunțul din poze și verificare KYC pentru tranzacții 100% sigure.
              </p>

              {/* Search bar */}
              <form
                action="/category/all"
                method="get"
                className="flex flex-col sm:flex-row gap-2 max-w-2xl mx-auto mb-5"
                data-testid="hero-search-form"
              >
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    name="search"
                    type="text"
                    placeholder="Ce cauți? (mașină, apartament, telefon, locuri de muncă...)"
                    className="w-full h-12 pl-11 pr-4 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 focus:border-blue-500/50 focus:outline-none text-white placeholder:text-slate-500 text-sm"
                    data-testid="hero-search-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="h-12 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-fuchsia-600 hover:from-blue-500 hover:to-fuchsia-500 text-white font-bold shadow-lg shadow-blue-500/30"
                  data-testid="hero-search-btn"
                >
                  Caută
                </Button>
              </form>

              {/* Trust pills */}
              <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
                {[
                  { icon: ShieldCheck, label: "Verificat KYC", color: "emerald" },
                  { icon: Zap, label: "Plată Escrow", color: "blue" },
                  { icon: Users, label: "Comunitate activă", color: "fuchsia" },
                  { icon: MapPin, label: "Toată țara", color: "amber" },
                ].map((it) => (
                  <span
                    key={it.label}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-slate-300 font-medium"
                  >
                    <it.icon className={`w-3.5 h-3.5 text-${it.color}-400`} />
                    {it.label}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Banner carousel below hero */}
          {banners.length > 0 && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
              <BannerCarousel banners={banners} />
            </div>
          )}
        </section>

        {/* Live Stats Bar */}
        <section className="px-4 sm:px-6 lg:px-8 mt-4 sm:-mt-6 relative z-10">
          <div className="max-w-7xl mx-auto">
            <div className="rounded-2xl border border-white/10 bg-gradient-to-r from-[#0a0a0a]/95 via-[#0e0e12]/95 to-[#0a0a0a]/95 backdrop-blur-xl p-3 sm:p-4 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 shadow-2xl shadow-black/50" data-testid="live-stats-bar">
              {[
                { icon: Activity, label: "Anunțuri active", value: stats.active_ads, iconBg: "bg-emerald-500/10", iconBorder: "border-emerald-500/20", iconColor: "text-emerald-400", dot: "bg-emerald-400", pulse: true },
                { icon: Flame, label: "Noi săptămâna asta", value: stats.new_this_week, iconBg: "bg-amber-500/10", iconBorder: "border-amber-500/20", iconColor: "text-amber-400", dot: "bg-amber-400", pulse: false },
                { icon: Gavel, label: "Licitații live", value: stats.live_auctions, iconBg: "bg-fuchsia-500/10", iconBorder: "border-fuchsia-500/20", iconColor: "text-fuchsia-400", dot: "bg-fuchsia-400", pulse: stats.live_auctions > 0 },
                { icon: ShieldCheck, label: "Verificați KYC", value: stats.verified_users, iconBg: "bg-blue-500/10", iconBorder: "border-blue-500/20", iconColor: "text-blue-400", dot: "bg-blue-400", pulse: false },
              ].map((s) => (
                <div
                  key={s.label}
                  className="group relative flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 rounded-xl hover:bg-white/5 transition-colors"
                >
                  <div className={`relative w-9 h-9 sm:w-10 sm:h-10 rounded-xl ${s.iconBg} border ${s.iconBorder} flex items-center justify-center shrink-0`}>
                    <s.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${s.iconColor}`} />
                    {s.pulse && (
                      <>
                        <span className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${s.dot} animate-ping`} />
                        <span className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${s.dot}`} />
                      </>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="text-base sm:text-xl font-black text-white leading-tight tabular-nums">
                      {s.value > 0 ? `${s.value.toLocaleString("ro-RO")}+` : "0"}
                    </div>
                    <div className="text-[10px] sm:text-xs text-slate-400 leading-tight truncate">{s.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
                  {t('popularCategories')}
                </h2>
                <p className="text-slate-400">
                  {t('findWhatYouNeed')}
                </p>
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
                {[...Array(6)].map((_, i) => (
                  <div 
                    key={i}
                    className={`rounded-2xl bg-[#0A0A0A] animate-pulse ${
                      i < 2 ? "md:col-span-2 md:row-span-2 aspect-video" : "aspect-[4/3]"
                    }`}
                  />
                ))}
              </div>
            ) : (
              <CategoryGrid categories={categories} />
            )}
          </div>
        </section>

        {/* Promoted Ads Section */}
        {promotedAds.length > 0 && (
          <section className="px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-20 bg-[#0A0A0A]">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6 sm:mb-8">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-600/20 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white">
                      {t('featuredAds')}
                    </h2>
                    <p className="text-slate-400 text-xs sm:text-sm">
                      {t('popularOffers')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                {promotedAds.map((ad) => (
                  <AdCard key={ad.ad_id} ad={ad} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Top-Rated Sellers Widget — auto-hides if no qualified sellers */}
        <TopSellersCarousel />

        {/* Recent Ads Section */}
        <section className="px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-20">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6 sm:mb-8">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-emerald-600/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-500" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white">
                    {t('recentAds')}
                  </h2>
                  <p className="text-slate-400 text-xs sm:text-sm">
                    {t('newestAds')}
                  </p>
                </div>
              </div>
              <Link to="/category/all">
                <Button 
                  variant="ghost" 
                  className="text-slate-400 hover:text-white hover:bg-white/5 text-sm sm:text-base"
                >
                  <span className="hidden sm:inline">{t('viewAll')}</span>
                  <ArrowRight className="w-4 h-4 sm:ml-2" />
                </Button>
              </Link>
            </div>

            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                {[...Array(8)].map((_, i) => (
                  <div 
                    key={i}
                    className="rounded-xl sm:rounded-2xl bg-[#0A0A0A] aspect-[4/3] animate-pulse"
                  />
                ))}
              </div>
            ) : recentAds.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {recentAds.map((ad) => (
                  <AdCard key={ad.ad_id} ad={ad} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 rounded-2xl bg-[#0A0A0A] border border-white/5">
                <p className="text-slate-400 mb-4">{t('noAds')}</p>
                <Link to="/create-ad">
                  <Button className="bg-blue-600 hover:bg-blue-500 text-white">
                    {t('addFirstAd')}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="max-w-4xl mx-auto">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600/20 via-purple-600/10 to-transparent border border-white/10 p-8 md:p-12 text-center">
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl" />
              
              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  {t('haveToSell')}
                </h2>
                <p className="text-slate-400 text-lg mb-8 max-w-xl mx-auto">
                  {t('postInMinutes')}
                </p>
                <Link to="/create-ad">
                  <Button className="h-14 px-8 text-lg rounded-full bg-blue-600 hover:bg-blue-500 text-white font-medium shadow-[0_0_30px_rgba(59,130,246,0.4)] transition-all duration-300 hover:shadow-[0_0_40px_rgba(59,130,246,0.6)]">
                    <TrendingUp className="w-5 h-5 mr-2" />
                    {t('publishFree')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section - Premium AEO */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 lg:py-20 bg-[#0A0A0A]">
          <AEOContent
            title="Tot ce trebuie să știi despre X67 Digital"
            subtitle="Răspunsuri optimizate pentru Google AI Overview, ChatGPT, Perplexity și Claude. Întrebări selectate din căutări reale."
            questions={HOMEPAGE_AEO_QUESTIONS}
            data-testid="homepage-aeo"
          />

          {/* SEO long-form content block (visible to crawlers + users) */}
          <div className="max-w-4xl mx-auto mt-16 prose prose-invert prose-sm sm:prose-base">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 flex items-center gap-2" data-speakable="true">
              <span className="inline-block w-1 h-6 rounded-full bg-gradient-to-b from-emerald-500 to-cyan-500" />
              Marketplace românesc în 2026 — de ce X67?
            </h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              <strong className="text-white">X67 Digital Media Groupe</strong> este platforma cu cea mai rapidă creștere în segmentul de anunțuri online din România. Spre deosebire de site-urile clasice (OLX, Publi24, Lajumate), X67 combină <strong>tehnologii AI de ultimă generație</strong> (Gemini 3 Flash Vision pentru generarea automată a anunțurilor din poze, AI face-match pentru verificarea identității) cu <strong>infrastructură financiară modernă</strong> (Escrow integrat cu Viva Wallet, plăți sigure RON/EUR).
            </p>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed mt-3">
              Acoperim <strong className="text-white">toate cele 42 de județe</strong> și <strong>368 de localități</strong> din România, cu hub-uri SEO dedicate pentru fiecare combinație categorie + oraș (București, Cluj-Napoca, Timișoara, Iași, Constanța, Brașov, Craiova, Oradea, Galați, Ploiești și încă 358). Categoriile principale acoperă <a href="/category/auto" className="text-emerald-300 hover:text-white transition-colors">mașini second hand</a>, <a href="/category/imobiliare" className="text-emerald-300 hover:text-white transition-colors">apartamente și case</a>, <a href="/category/electronice" className="text-emerald-300 hover:text-white transition-colors">electronice</a>, <a href="/category/locuri_munca" className="text-emerald-300 hover:text-white transition-colors">locuri de muncă</a>, <a href="/category/moda" className="text-emerald-300 hover:text-white transition-colors">modă</a>, <a href="/category/servicii" className="text-emerald-300 hover:text-white transition-colors">servicii</a>, <a href="/category/animale" className="text-emerald-300 hover:text-white transition-colors">animale</a> plus 5 categorii noi de specialty (Colecționabile, Mobilă, Sport, Construcții, Mamă & Copil).
            </p>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed mt-3">
              Funcționalitățile premium care ne diferențiază: <strong className="text-white">Arena de licitații live</strong> cu countdown pulsant și anti-sniping, <strong>chat real-time WebSocket</strong> cu typing indicator și read receipts, <strong>verificare KYC instant</strong> cu AI Gemini Vision, <strong>alerte email pentru căutări salvate</strong>, <strong>sistem de comparare anunțuri</strong> side-by-side, <strong>oferte de preț</strong> (negociere directă), <strong>reviews și rating</strong> 5 stele pentru fiecare vânzător, <strong>PWA installable</strong> pe iOS și Android cu push notifications și offline mode, plus <strong>Enterprise Seller API + Webhooks</strong> pentru magazine.
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
